<template>
  <fts-client-modal
    :append-to-body="appendToBody"
    title="My Calendar"
    description="Create a calendar event in my calendar."
    append-content-class="my-calendar-form-modal__body"
    class="fts-my-calendar-form-modal"
    @closed="reset"
    ref="ftsClientModal"
  >
    <!-- header:icon -->
    <font-awesome-icon slot="icon" :icon="['far', 'calendar-plus']" />
    <!-- body -->
    <template #body>
      <!-- selection -->
      <div class="fts-form-group">
        <div class="mc-form-selection">
          <!-- start date -->
          <div class="mc-form-selection-label">start from</div>
          <fts-select
            v-model="formData.startId"
            :options="options"
            label="displayName"
            placeholder="start date"
            comfort-design
            @input="validatorTouch('startId')"
            @search:blur="validatorTouch('startId')"
            class="mc-form-selection-control"
          />
          <!-- end date -->
          <div class="mc-form-selection-label">until</div>
          <fts-select
            v-model="formData.endId"
            :options="options"
            label="displayName"
            placeholder="end date (optional)"
            comfort-design
            class="mc-form-selection-control"
          />
        </div>
        <div class="mc-form-error" v-if="validatorError('startId')">start date is required.</div>
      </div>
      <!-- everyone -->
      <div class="fts-form-group mc-form-group">
        <div class="mc-form-label mr">for</div>
        <div class="mc-form-switch">
          <div
            class="mc-form-switch-option"
            :class="{'mc-form-switch--active': !formData.everyone}"
            @click="toggleEveryone()"
          >
            myself
          </div>
          <div
            class="mc-form-switch-option"
            :class="{'mc-form-switch--active': formData.everyone}"
            @click="toggleEveryone(true)"
          >
            everyone
          </div>
        </div>
      </div>
      <!-- color -->
      <!-- <div class="fts-form-group mc-form-group">
        <div class="mc-form-label mr">label as</div>
        <swatches
          row-length="5"
          :swatches="colors"
          shapes="circles"
          popover-x="right"
          popover-y="top"
          :swatch-size="25" 
          :trigger-style="{ width: '18px', height: '18px' }"
          v-model="formData.color"
          class="mc-form-swatches" 
        />
      </div> -->
    </template>
    <!-- footer:right -->
    <template #footerRight>
      <fts-btn
        text
        :disabled="submitting"
        @click="close"
      >
        <font-awesome-icon :icon="['fas', 'times']" /> Close
      </fts-btn>

      <fts-btn
        color="primary"
        :loading="submitting"
        @click.native.prevent="done"
      >
        <font-awesome-icon :icon="['fas', 'check']" /> Done
      </fts-btn>
    </template>
  </fts-client-modal>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);
import { required } from "vuelidate/lib/validators";
import Swatches from "vue-swatches";

import { clientStore } from "../store/clientStore";
import { darkPalette } from "../commons/colors";

import FTSClientModal from "./FTSClientModal";
import FTSSelect from "./FTSSelect";
import FTSBtn from "./FTSBtn";

export default {
  components: {
    Swatches,
    "fts-client-modal": FTSClientModal,
    "fts-select": FTSSelect,
    "fts-btn": FTSBtn,
  },
  props: {
    appendToBody: Boolean,
    workspaceId: [String, Number],
    moduleId: [String, Number],
    listId: String,
    options: Array,
  },
  data: () => ({
    formData: {},
    resolve: null,
    submitting: false,
    colors: darkPalette,
  }),
  validations() {
    let formData = {};
    formData.startId = { required };
    return { formData };
  },
  methods: {
    async show() {
      return new Promise((resolve) => {
        this.resolve = resolve;
        this.$refs.ftsClientModal.show();
      });
    },
   async done() {
      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        this.submitting = true;
        try {
          await clientStore.dispatch("setMyCalendarEvent", {
            workspaceId: this.workspaceId,
            vectorId: this.moduleId,
            listId: this.listId,
            startId: this.formData.startId,
            endId: this.formData.endId,
            everyone: this.formData.everyone,
            color: this.formData.color,
          });
          this.$toasted.success("Calendar was successfully added.");
          this.resolve && this.resolve(this.formData);
          this.close();
        } catch(err) {
          this.$toasted.global.error({ response: err.response });
        }
        this.submitting = false;
      } else {
        console.warn("Form invalid", this.$v.formData)
      }
    },
    close() {
      this.$refs.ftsClientModal.close();
    },
    reset() {
      this.$v.$reset();
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    toggleEveryone(arg) {
      if (arg)
        this.$set(this.formData, 'everyone', true);
      else
        this.$delete(this.formData, 'everyone');
    },
    validatorTouch(field) {
      return this.$v.formData && this.$v.formData[field] && this.$v.formData[field].$touch();
    },
    validatorError(field) {
      return this.$v.formData && this.$v.formData[field] && this.$v.formData[field].$error;
    }
  },
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-my-calendar-form-modal {
  .my-calendar-form-modal__body {
    padding-top: 2rem;
  }
  .mc-form-group {
    display: flex;
    align-items: center;
  }
  .mc-form-label {
    font-size: .875rem;
    color: var(--fts-form-label);
    &.mr {
      margin-right: .75rem;
    }
  }
  .mc-form-error {
    margin-top: .375rem;
    font-size: .75rem;
    color: var(--fts-danger);
  }
  .mc-form-selection {
    @include flex-gap($display: flex, $gap: .75rem);
    align-items: center;
    .mc-form-selection-label {
      font-size: .875rem;
      color: var(--fts-form-label);
    }
    .mc-form-selection-control {
      flex: 1 1 215px;
      max-width: 215px;
    }
  }
  .mc-form-switch {
    display: flex;
    align-items: center;
    .mc-form-switch-option {
      display: inline-flex;
      padding: 0.575rem 1rem;
      background-color: var(--fts-gray-100);
      border-radius: .25rem;
      color: var(--fts-gray-400);
      font-size: .875rem;
      font-weight: 500;
      cursor: pointer;
      &:not(:first-child) {
        margin-left: .375rem;
      }
      &.mc-form-switch--active {
        @include theme() {
          background: rgba(theme-get('fts-primary'), 0.095);
        }
        color: var(--fts-primary);
      }
    }
  }
  .mc-form-swatches {
    display: inline-flex;
  }
}
</style>